import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import EventsFeed from './pages/EventsFeed';
import LiveManagement from './pages/LiveManagement';
import CreateEvent from './pages/CreateEvent';
import EditEvent from './pages/EditEvent';
import EventDetail from './pages/EventDetail';
import Login from './pages/Login';
import Clubs from './pages/Clubs';
import HowItWorks from './pages/HowItWorks';
import Signup from './pages/Signup';
import CheckoutSubscription from './pages/CheckoutSubscription';
import ComingSoon from './pages/ComingSoon';
import PricingLandingPage from './pages/PricingLandingPage';
import Navbar from './components/shared/Navbar';
import Footer from './components/shared/Footer';
import DashboardLayout from './components/dashboard/DashboardLayout';
import LandingPage from './pages/LandingPage';
import DashboardHome from './pages/dashboard/DashboardHome';
import ManageClubs from './pages/dashboard/ManageClubs';
import ManageEvents from './pages/dashboard/ManageEvents';
import ManagePlayers from './pages/dashboard/ManagePlayers';
import Finance from './pages/dashboard/Finance';
import Settings from './pages/dashboard/Settings';
import MigrateRegistrations from './pages/MigrateRegistrations';
import ProtectedRoute from './components/shared/ProtectedRoute';
import ClubLayout from './layouts/ClubLayout';
import ClubLandingPage from './pages/ClubLandingPage';
import OrganizerMiniSite from './pages/OrganizerMiniSite';
import EventDetailWhitelabel from './pages/EventDetailWhitelabel';

import CookieConsent from './components/shared/CookieConsent';

import ScrollToTop from './components/shared/ScrollToTop';

// Master Dashboard Imports
import MasterLayout from './components/master/MasterLayout';
import MasterDashboardHome from './pages/master/MasterDashboardHome';
import ManageOrganizers from './pages/master/ManageOrganizers';
import ManageEventsGlobal from './pages/master/ManageEventsGlobal';
import ManageUsersGlobal from './pages/master/ManageUsersGlobal';
import ManageClubsGlobal from './components/master-dashboard/ManageClubsGlobal';
import ManageSubscriptions from './pages/master/ManageSubscriptions';
import MaintenanceGate from './components/shared/MaintenanceGate';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { logout } from './services/api';

function App() {
    // Sincronización de Rol y Datos de Usuario
    React.useEffect(() => {
        const syncUserRole = async () => {
            const userId = localStorage.getItem('userId');
            const storedEmail = localStorage.getItem('userEmail');
            const storedRole = localStorage.getItem('userRole');

            if (!userId) {
                console.log("ℹ️ [Visiopadel Auth] No hay sesión activa.");
                return;
            }

            console.log("🔍 [Visiopadel Auth] Verificando sesión local:", { 
                email: storedEmail, 
                uid: userId, 
                role: storedRole 
            });

            try {
                const userDocRef = doc(db, 'users', userId);
                console.log("📡 [Visiopadel Auth] Solicitando documento a Firestore...");
                const userDocSnap = await getDoc(userDocRef);
                console.log("📥 [Visiopadel Auth] Respuesta recibida de Firestore");
                
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                console.log("✅ [Visiopadel Auth] Datos de usuario recuperados:", userData);
                    console.log("✅ [Visiopadel Auth] Datos recuperados de Firestore:", { 
                        role: userData.role, 
                        email: userData.email,
                        name: `${userData.firstName || ''} ${userData.lastName || ''}`
                    });

                    if (userData.disabled) {
                        console.warn("🚫 [Visiopadel Auth] Usuario deshabilitado. Cerrando sesión...");
                        logout(); // No need to await here as it redirects
                        return;
                    }

                    if (userData.role && userData.role !== storedRole) {
                        console.log("🚀 [Visiopadel Auth] ¡Cambio detectado! Sincronizando:", userData.role);
                        localStorage.setItem('userRole', userData.role);
                        localStorage.setItem('userName', `${userData.firstName || ''} ${userData.lastName || ''}`);
                        window.location.reload();
                    }
                } else {
                    console.error("❌ [Visiopadel Auth] ERROR: No existe documento en /users/ con ID:", userId);
                    console.log("💡 Tip: Verifica que el UID en Firebase Console sea exactamente este.");
                }
            } catch (error) {
                console.error("❌ [Visiopadel Auth] Error en la consulta de Firestore:", error);
            }
        };

        syncUserRole();
    }, []);

    return (
        <>
            <ScrollToTop />
            <MaintenanceGate>
                <Routes>
                    {/* Landing Page Principal */}
                    <Route path="/" element={<LandingPage />} />

                    {/* Espacios de Club (Whitelabel) */}
                    <Route path="/c/:slug" element={<ClubLayout />}>
                        <Route index element={<ClubLandingPage />} />
                        <Route path="events" element={<ClubLandingPage />} />
                    </Route>

                    {/* Rutas Públicas de la Web */}
                    <Route path="/events" element={
                        <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                            <Navbar />
                            <main className="flex-grow">
                                <EventsFeed />
                            </main>
                            <Footer />
                        </div>
                    } />

                    <Route path="/events/:eventId" element={
                        <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                            <Navbar />
                            <main className="flex-grow">
                                <EventDetail />
                            </main>
                            <Footer />
                        </div>
                    } />

                    <Route path="/events/:eventId/live" element={
                        <LiveManagement />
                    } />

                    <Route path="/events/create" element={
                        <ProtectedRoute allowedRoles={['organizer', 'super_admin']}>
                            <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                                <Navbar />
                                <main className="flex-grow">
                                    <CreateEvent />
                                </main>
                                <Footer />
                            </div>
                        </ProtectedRoute>
                    } />

                    <Route path="/events/edit/:eventId" element={
                        <ProtectedRoute allowedRoles={['organizer', 'super_admin']}>
                            <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                                <Navbar />
                                <main className="flex-grow">
                                    <EditEvent />
                                </main>
                                <Footer />
                            </div>
                        </ProtectedRoute>
                    } />

                    <Route path="/clubs" element={
                        <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                            <Navbar />
                            <main className="flex-grow">
                                <ComingSoon 
                                    title="Gestión de Clubes"
                                    description="Estamos desarrollando una plataforma completa para gestionar tu club de pádel. Pronto podrás administrar reservas, socios y mucho más."
                                    feature="gestión de clubes"
                                />
                            </main>
                            <Footer />
                        </div>
                    } />

                    <Route path="/clubs/:clubId" element={
                        <div className="min-h-screen bg-gray-50 flex flex-col pt-24">
                            <Navbar />
                            <main className="flex-grow">
                                <ComingSoon 
                                    title="Ficha del Club"
                                    description="Pronto podrás ver toda la información detallada de este club, sus instalaciones y servicios."
                                    feature="ficha de club"
                                />
                            </main>
                            <Footer />
                        </div>
                    } />

<Route path="/pricing" element={<PricingLandingPage />} />

<Route path="/about" element={<HowItWorks />} />

<Route path="/login" element={<Login />} />

<Route path="/signup" element={<Signup />} />
    
                    <Route path="/checkout-subscription" element={
                        <CheckoutSubscription />
                    } />

                    {/* Rutas del DASHBOARD/CRM (Layout Independiente) - PROTEGIDAS */}
                    <Route path="/dashboard/*" element={
                        <ProtectedRoute allowedRoles={['organizer', 'super_admin']}>
                            <DashboardLayout>
                                <Routes>
                                    <Route path="/" element={<DashboardHome />} />
                                    <Route path="/events" element={<ManageEvents />} />
                                    <Route path="/clubs" element={<ManageClubs />} />
                                    <Route path="/players" element={<ManagePlayers />} />
                                    <Route path="/finance" element={<Finance />} />
                                    <Route path="/settings" element={<Settings />} />
                                    <Route path="/migrate" element={<MigrateRegistrations />} />
                                </Routes>
                            </DashboardLayout>
                        </ProtectedRoute>
                    } />

                    {/* Rutas del MASTER DASHBOARD (SaaS Owner) - PROTEGIDAS */}
                    <Route path="/master-admin/*" element={
                        <ProtectedRoute allowedRoles={['super_admin']}>
                            <MasterLayout>
                                <Routes>
                                    <Route path="/" element={<MasterDashboardHome />} />
                                    <Route path="organizers" element={<ManageOrganizers />} />
                                    <Route path="clubs" element={<ManageClubsGlobal />} />
                                    <Route path="events" element={<ManageEventsGlobal />} />
                                    <Route path="subscriptions" element={<ManageSubscriptions />} />
                                    <Route path="users" element={<ManageUsersGlobal />} />
                                    <Route path="settings" element={<ComingSoon title="Configuración SaaS" description="Ajustes técnicos globales de la plataforma." feature="settings" />} />
                                    <Route path="*" element={<Navigate to="organizers" replace />} />
                                </Routes>
                            </MasterLayout>
                        </ProtectedRoute>
                    } />

                    {/* Ruta de Mini-site de Organizador (White-label) */}
                    <Route path="/o/:slug" element={<OrganizerMiniSite />} />
                    <Route path="/o/:slug/events/:eventId" element={<EventDetailWhitelabel />} />

                    {/* 404 */}
                    <Route
                        path="*"
                        element={
                            <div className="min-h-screen flex items-center justify-center">
                                <div className="text-center">
                                    <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
                                    <p className="text-xl text-gray-600 mb-6">Página no encontrada</p>
                                    <Link
                                        to="/"
                                        className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                                    >
                                        Volver al inicio
                                    </Link>
                                </div>
                            </div>
                        }
                    />
                </Routes>
            </MaintenanceGate>
            <CookieConsent />
        </>
    );
}

export default App;
